import React, { useState } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Typography,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { styled } from '@mui/material/styles';
import MediaControls from './MediaControls';
import { validateVideoDuration } from '../utils/video-util';

const PostBox = styled('div')(({theme, disabled}) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1),
    },
    ...(disabled && {
        backgroundColor: theme.palette.action.disabledBackground,
        opacity: 0.7,
    })
}));

const CharacterWarning = styled(Typography)`
    color: ${props => props.theme.palette.error.main};
    text-align: right;
    font-size: 0.875rem;
    margin-top: 4px;
`;

const InputWrapper = styled('div')(({theme}) => ({
    display: 'flex',
    gap: '24px',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: '16px',
    }
}));

/**
 * ThreadPostEditor Component
 * Handles editing of individual thread posts with media support
 * Encapsulates post content, media, and validation logic
 */
const ThreadPostEditor = ({
    post,
    index,
    characterLimit,
    canRemove = true,
    onUpdateText,
    onUpdateMedia,
    onUpdateAltText,
    onRemovePost,
    onFileUpload,
    onImageGalleryOpen,
    selectedAccounts = [],
    isLast = false
}) => {
    const [videoError, setVideoError] = useState('');

    const handleTextChange = (e) => {
        onUpdateText(index, e.target.value);
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const isVideo = file.type.startsWith('video/');
            
            if (isVideo) {
                const selectedPlatforms = selectedAccounts.map(account => account.platform.toUpperCase());
                
                const { isValid, error } = await validateVideoDuration(
                    file, 
                    selectedPlatforms
                );
                
                if (!isValid) {
                    setVideoError(error);
                } else {
                    setVideoError('');
                }
            }

            const url = URL.createObjectURL(file);
            const fileType = file.type.split('/')[1];
            
            const mediaData = {
                url: url,
                type: fileType === 'mp4' ? 'mp4' : 
                      fileType === 'gif' ? 'gif' : 'png',
                file: file
            };

            onUpdateMedia(index, mediaData);
            
            if (onFileUpload) {
                onFileUpload(event, index);
            }
            
        } catch (error) {
            console.error('Error handling file upload:', error);
            setVideoError('Failed to process video file');
        }
    };

    const handleRemoveMedia = () => {
        if (post.media_url) {
            URL.revokeObjectURL(post.media_url);
        }
        onUpdateMedia(index, { url: '', type: '', file: null });
        setVideoError('');
    };

    const handleAltTextChange = (e) => {
        onUpdateAltText(index, e.target.value);
    };

    const handleImageGalleryOpen = () => {
        if (onImageGalleryOpen) {
            onImageGalleryOpen(index);
        }
    };

    const isOverLimit = post.text.length > characterLimit;
    const showCharacterWarning = post.text.length > characterLimit - 20;

    return (
        <PostBox>
            {/* Post Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="subtitle1">
                        Post {index + 1}
                        {index === 0 && " (Thread Start)"}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                    {canRemove && (
                        <IconButton
                            size="small"
                            onClick={() => onRemovePost(index)}
                            color="error"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Main Content - Media and Text Input */}
            <InputWrapper>
                {/* Media Preview */}
                {post.media_url && (
                    <div style={{width: '300px'}}>
                        {post.media_type === 'mp4' ? (
                            <>
                                <video width="100%" height="100%" controls>
                                    <source src={post.media_url} type="video/mp4"/>
                                    Your browser does not support the video tag.
                                </video>
                                {videoError && (
                                    <Typography 
                                        color="error" 
                                        variant="caption" 
                                        sx={{ 
                                            display: 'block', 
                                            mt: 1,
                                            textAlign: 'center' 
                                        }}
                                    >
                                        {videoError}
                                    </Typography>
                                )}
                            </>
                        ) : post.media_type === 'gif' ? (
                            <img src={post.media_url} alt="Generated GIF"
                                 style={{width: '100%', height: '100%'}}/>
                        ) : (
                            <img height="100%" width="100%"
                                 src={post.media_url} alt="Generated Image"/>
                        )}
                        <InputWrapper>
                            <TextField
                                label='Image Description'
                                variant="outlined"
                                fullWidth
                                value={post.alt_text || ''}
                                onChange={handleAltTextChange}
                            />
                        </InputWrapper>
                    </div>
                )}

                {/* Text Input Area */}
                <div style={{width: '100%'}}>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        placeholder={index === 0 ? "Start your thread..." : "Continue your thread..."}
                        value={post.text}
                        onChange={handleTextChange}
                        variant="outlined"
                        error={isOverLimit}
                    />
                    
                    {/* Character count warning */}
                    {showCharacterWarning && (
                        <CharacterWarning isOverLimit={isOverLimit}>
                            {post.text.length}/{characterLimit} characters
                            {isOverLimit && ' - Exceeds limit!'}
                        </CharacterWarning>
                    )}
                </div>
            </InputWrapper>

            {/* Media Controls */}
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                <MediaControls
                    hasMedia={!!post.media_url}
                    onFileUpload={handleFileUpload}
                    onRemoveMedia={handleRemoveMedia}
                    onImageGalleryOpen={handleImageGalleryOpen}
                    compact={true}
                />
            </Box>
            
            {/* Connection indicator for non-last posts */}
            {!isLast && (
                <Box mt={2}>
                    <Divider />
                    <Box 
                        display="flex" 
                        alignItems="center" 
                        justifyContent="center" 
                        mt={1}
                        sx={{ 
                            color: 'text.secondary',
                            fontSize: '0.875rem'
                        }}
                    >
                        ↓ Continues in next post
                    </Box>
                </Box>
            )}
        </PostBox>
    );
};

export default ThreadPostEditor; 